import { isHardNavEnabled } from "./hard-nav";

// Capture-phase click handler that turns a same-origin in-app link click into a
// full-page navigation, so it re-enters the storefront-traffic-bridge edge
// router instead of Gatsby's client-side router (which would serve a stale
// marketing-frontend page, or its 404, for a path already migrated to MWF).
// Exported for unit testing.
export function handleHardNavClick(event: MouseEvent): void {
  // Let the browser handle anything that isn't a plain left-click, and any
  // click a handler upstream already claimed.
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }

  const anchor = (event.target as HTMLElement | null)?.closest("a");
  if (!anchor) return;

  // Reserved target keywords are ASCII case-insensitive; any target other than
  // _self (a new tab/window/frame or a named context) keeps its default handling.
  const target = anchor.target.trim().toLowerCase();
  if (
    (target && target !== "_self") ||
    anchor.hasAttribute("download") ||
    anchor.relList.contains("external") ||
    // Escape hatch: a component that drives its own click handling (e.g. opens a
    // modal and calls preventDefault) marks the link, or an ancestor, so this
    // capture-phase listener leaves it alone — otherwise our preventDefault()
    // fires before that handler runs and it can no longer cancel the navigation.
    anchor.closest("[data-no-hard-nav]")
  ) {
    return;
  }

  const href = anchor.getAttribute("href");
  if (!href || href.startsWith("#")) return;

  const url = new URL(anchor.href, window.location.href);
  // External link: leave the browser default alone.
  if (url.origin !== window.location.origin) return;
  // In-page hash link: let the browser scroll, don't reload.
  if (
    url.pathname === window.location.pathname &&
    url.search === window.location.search &&
    url.hash
  ) {
    return;
  }

  if (!isHardNavEnabled()) return; // gate off -> Gatsby client-side routing

  // preventDefault() also makes @reach/router's Link skip navigate() (it bails
  // on event.defaultPrevented), so this both stops the browser default and
  // suppresses client-side routing while leaving other click handlers (e.g.
  // analytics) to run.
  event.preventDefault();
  window.location.assign(url.href);
}

// Covers every Gatsby <Link> / styled(Link) at once (they all render a real
// <a href>), so no per-link edit is needed and links added later are covered
// automatically. Programmatic navigate() (search) has no anchor to catch and is
// handled at its call sites.
export function installHardNavInterceptor(): void {
  // capture: run before @reach/router's bubble-phase handler
  document.addEventListener("click", handleHardNavClick, true);
}
